import VariantPicker from '@theme/variant-picker';
import { MORPH_OPTIONS, morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';
import {
  PDP_PRODUCT_INFORMATION_SELECTOR,
  getGcoGalleryMorphPair,
  getGcoOverlayMorphPair,
  getGcoPdpPlacementMorphPairs,
  getGcoPriceMorphPair,
  getGcoTitleAboveMorphPair,
  getGcoTitleBesideMorphPair,
  isGcoProductCard,
  rewriteGcoSectionId,
} from './custom-gco-product-card-helpers.js';

const ORIGINAL_BUILD_REQUEST_URL = Symbol.for('gco.variant-picker.buildRequestUrl');
const ORIGINAL_UPDATE_PRICE = Symbol.for('gco.product-card.updatePrice');
const PDP_PLACEMENT_ADAPTER = Symbol.for('gco.pdpPlacement.adapter');
const GCO_ROOT_MORPH_OPTIONS = {
  ...MORPH_OPTIONS,
  childrenOnly: false,
};

function morphGcoRootPair(morphPair) {
  morph(morphPair.current, morphPair.next, GCO_ROOT_MORPH_OPTIONS);
}

if (!VariantPicker.prototype[ORIGINAL_BUILD_REQUEST_URL]) {
  Object.defineProperty(VariantPicker.prototype, ORIGINAL_BUILD_REQUEST_URL, {
    value: VariantPicker.prototype.buildRequestUrl,
  });

  VariantPicker.prototype.buildRequestUrl = function buildGcoRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    const buildRequestUrl = this[ORIGINAL_BUILD_REQUEST_URL];
    const requestUrl = buildRequestUrl.call(this, selectedOption, source, sourceSelectedOptionsValues);

    if (!isGcoProductCard(this)) return requestUrl;

    return rewriteGcoSectionId(requestUrl);
  };
}

export async function installGcoProductCardAdapter() {
  const ProductCard = await customElements.whenDefined('product-card');

  if (ProductCard.prototype[ORIGINAL_UPDATE_PRICE]) return;

  Object.defineProperty(ProductCard.prototype, ORIGINAL_UPDATE_PRICE, {
    value: ProductCard.prototype.updatePrice,
  });

  ProductCard.prototype.updatePrice = function updateGcoPrice(html) {
    const updatePrice = this[ORIGINAL_UPDATE_PRICE];

    if (!isGcoProductCard(this)) {
      return updatePrice.call(this, html);
    }

    const priceMorphPair = getGcoPriceMorphPair(this, html);

    if (priceMorphPair) {
      morphGcoRootPair(priceMorphPair);
    }

    const overlayMorphPair = getGcoOverlayMorphPair(this, html);

    if (overlayMorphPair) {
      morphGcoRootPair(overlayMorphPair);
    }

    const galleryMorphPair = getGcoGalleryMorphPair(this, html);

    if (galleryMorphPair) {
      morphGcoRootPair(galleryMorphPair);
    }

    const titleAboveMorphPair = getGcoTitleAboveMorphPair(this, html);

    if (titleAboveMorphPair) {
      morphGcoRootPair(titleAboveMorphPair);
    }

    const titleBesideMorphPair = getGcoTitleBesideMorphPair(this, html);

    if (titleBesideMorphPair) {
      morphGcoRootPair(titleBesideMorphPair);
    }
  };
}

export function installGcoPdpPlacementAdapter() {
  if (document[PDP_PLACEMENT_ADAPTER]) return;

  Object.defineProperty(document, PDP_PLACEMENT_ADAPTER, {
    value: true,
  });

  document.addEventListener(StandardEvents.productSelect, (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const productInformation = event.target.closest(PDP_PRODUCT_INFORMATION_SELECTOR);
    if (!productInformation) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && productInformation.querySelector('product-price')?.dataset.productId !== detail.productId) return;

        const morphPairs = getGcoPdpPlacementMorphPairs(productInformation, detail.html);
        for (const morphPair of morphPairs) {
          morphGcoRootPair(morphPair);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[gco-placement] Event promise rejected:', error);
      });
  });
}

installGcoPdpPlacementAdapter();
installGcoProductCardAdapter();
